import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, DatePicker, Select, Alert, Statistic, Tag, Button, Space, Empty, message, Tooltip,
} from 'antd';
import { ReloadOutlined, UserOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { getCompetitionCollections } from '../../api/competitions';
import { getBranches } from '../../api/branches';
import { tahsilatZamani, saatiYok } from '../../utils/tahsilatZamani';
import apiErrorMessage from '../../utils/apiError';

const { RangePicker } = DatePicker;

const YONTEM = { nakit: 'Nakit', havale: 'Havale/EFT', kart: 'Kart', diger: 'Diğer' };

const para = (v) => Number(v || 0).toLocaleString('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const adSoyad = (k) => (k ? `${k.name} ${k.surname}` : '—');

/**
 * GÜN SONU MUTABAKATI — "bugün kim ne ödedi, ne kadar topladık".
 *
 * Eren Bey'in bildirdiği eksik (27.10.2026): gün sonunda öğretmenler normal
 * ders parasıyla yarışma parasını BİRLİKTE teslim ediyor. Normal tarafta
 * Finans > Ödemeler o günü listeleyip toplamı gösterdiği için elindeki
 * parayla karşılaştırabiliyordu; yarışma tarafında böyle bir ekran yoktu,
 * yani teslim alınan parayı hiçbir şeye dayandıramıyordu.
 *
 * ⚠ KAPSAMI SUNUCU BELİRLİYOR, bu bileşen DEĞİL. Yanıttaki `scope` alanı
 * `competitions.finance` olanda "all" (şubenin tamamı), olmayanda "own"
 * (yalnızca kendi tahsilatı). Buradaki metin ve filtreler o alana bakıyor —
 * `received_by` göndererek başkasının satırını çekmek mümkün değil, uç onu
 * yok sayıyor. Arayüzde gizlemek yetki değildir; burada gizlemiyoruz da,
 * yalnızca sunucunun verdiği kapsamı doğru anlatıyoruz.
 *
 * ⚠ TOPLAMLAR SAYFALAMADAN BAĞIMSIZ. Normal Ödemeler ekranındaki toplam
 * "Bu Sayfanın Toplamı" — gün filtresiyle liste kısa olduğu için pratikte
 * doğru çıkıyor. Mutabakatta yanlış toplam, hiç toplam olmamasından kötü
 * olduğu için buradaki toplamları sunucu tüm kümeden hesaplıyor.
 *
 * ⚠ KUR ÇEVRİLMİYOR — ₺ ile € tek rakama indirgenmiyor (modülün duran
 * kuralı). Her para birimi kendi kartında.
 */
const CompetitionCollectionsTab = () => {
  // Varsayilan BUGUN - ekranin isi gun sonu mutabakati.
  const [aralik, setAralik] = useState([dayjs(), dayjs()]);
  const [sube, setSube] = useState(null);
  const [subeler, setSubeler] = useState([]);
  const [tahsilEden, setTahsilEden] = useState(null);

  const [kayitlar, setKayitlar] = useState([]);
  const [ozet, setOzet] = useState({ currencies: {}, by_receiver: [] });
  const [kapsam, setKapsam] = useState(null);     // 'all' | 'own' - SUNUCUDAN
  const [yukleniyor, setYukleniyor] = useState(false);
  const [sayfa, setSayfa] = useState({ current: 1, pageSize: 50, total: 0 });

  const kendisi = kapsam === 'own';

  // Filtrelerin tamami bagimlilik dizisinde - biri atlanirsa sayfa
  // degisiminde o filtre sessizce sifirlanir (frontend/CLAUDE.md,
  // "useCallback stale closure").
  const getir = useCallback(async (istenenSayfa = 1, boyut = 50) => {
    if (!aralik?.[0] || !aralik?.[1]) return;
    setYukleniyor(true);
    try {
      const { data } = await getCompetitionCollections({
        // Tarih .format('YYYY-MM-DD') ile gonderilir - ham dayjs nesnesi
        // UTC'ye cevrilirken bir gun geriye kayar (CLAUDE.md).
        date_from: aralik[0].format('YYYY-MM-DD'),
        date_to: aralik[1].format('YYYY-MM-DD'),
        branch_id: sube || undefined,
        received_by: tahsilEden || undefined,
        page: istenenSayfa,
        per_page: boyut,
      });
      setKayitlar(data.data || []);
      setOzet(data.summary || { currencies: {}, by_receiver: [] });
      setKapsam(data.scope);
      setSayfa({
        current: data.current_page,
        pageSize: data.per_page,
        total: data.total,
      });
    } catch (e) {
      message.error(apiErrorMessage(e, 'Tahsilat listesi yüklenemedi.'));
    } finally {
      setYukleniyor(false);
    }
  }, [aralik, sube, tahsilEden]);

  // Filtre degisince ilk sayfadan basla. `sayfa.pageSize` bilerek
  // bagimlilik degil: yanittan geldigi icin eklenseydi her istek bir
  // sonrakini tetikler ve dongu olurdu.
  useEffect(() => {
    getir(1, sayfa.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getir]);

  // Sube secici YALNIZCA kapsami tam olanda ve gercekten birden fazla sube
  // varsa anlamli. Hata yutuluyor: `branches.view` izni olmayan biri de bu
  // ekrana girebilir, o zaman secici hic cizilmiyor.
  useEffect(() => {
    if (kapsam !== 'all' || subeler.length) return;
    getBranches({ per_page: 100 })
      .then((r) => setSubeler(r.data.data || []))
      .catch(() => {});
  }, [kapsam, subeler.length]);

  const sutunlar = [
    {
      title: 'Zaman',
      dataIndex: 'paid_at',
      width: 150,
      render: (v, k) => (
        <>
          <div>{tahsilatZamani(v)}</div>
          {/* Saat bilinmiyorsa (geriye donuk giris) kaydin GIRILDIGI an
              kucuk yaziliyor - itiraz durumunda bakilan deger bu. */}
          {saatiYok(v) && k.recorded_at && (
            <div className="text-xs text-gray-400">
              girildi: {dayjs(k.recorded_at).format('DD.MM.YYYY HH:mm')}
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Öğrenci',
      dataIndex: 'student',
      render: (o) => (o
        ? <Link to={`/students/${o.id}`}>{o.name} {o.surname}</Link>
        : '—'),
    },
    {
      title: 'Yarışma / Etap',
      dataIndex: 'competition',
      render: (v, k) => (
        <>
          <div>{v || '—'}</div>
          {k.stage && <div className="text-xs text-gray-500">{k.stage}</div>}
        </>
      ),
    },
    { title: 'Taksit', dataIndex: 'installment', width: 130, render: (v) => v || '—' },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      align: 'right',
      width: 140,
      render: (v, k) => (
        <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
          {para(v)} {k.currency}
        </strong>
      ),
    },
    {
      title: 'Yöntem',
      dataIndex: 'payment_method',
      width: 110,
      render: (v) => (v ? <Tag>{YONTEM[v] || v}</Tag> : '—'),
    },
    // Kendi tahsilatlarini goren kisi icin bu kolon her satirda AYNI ismi
    // yazardi - yer kaplamasin.
    ...(kendisi ? [] : [{
      title: 'Tahsil Eden',
      dataIndex: 'receiver',
      width: 160,
      render: (o) => adSoyad(o),
    }]),
    {
      title: 'Not',
      dataIndex: 'notes',
      ellipsis: true,
      render: (v) => (v ? <Tooltip title={v}>{v}</Tooltip> : null),
    },
  ];

  const paraBirimleri = Object.entries(ozet.currencies || {});
  const kirilim = ozet.by_receiver || [];

  return (
    <div className="space-y-4">
      <Alert
        type="info"
        showIcon
        message={kendisi ? 'Yalnızca kendi tahsilatlarınız' : 'Şubenin tüm yarışma tahsilatları'}
        description={kendisi
          ? 'Bu listede yalnızca sizin aldığınız yarışma tahsilatları görünür. Teslim ettiğiniz parayı buradaki toplamla karşılaştırabilirsiniz.'
          : 'Toplamlar sayfadaki satırlardan değil, filtreye uyan TÜM kayıtlardan hesaplanır. Kur çevrilmez — her para birimi ayrı toplanır.'}
      />

      <Card size="small">
        <Space wrap>
          <RangePicker
            value={aralik}
            // Yalnizca IKI ucu da dolu araligi kabul et. `setAralik`
            // dogrudan baglanirsa yari secili aralik da state'e giriyor ve
            // her tus icin bir istek gidiyor (olculdu: tek bir tarih
            // degisikligi uc istek uretti).
            onChange={(v) => { if (v?.[0] && v?.[1]) setAralik(v); }}
            format="DD.MM.YYYY"
            allowClear={false}
            // Kendi tahsilatini goren kisi icin tarih BUGUN'e sabit.
            // Kullanicinin karari (27.10.2026): ona verilen is "bugun ne
            // topladim" mutabakati. Bu yalnizca GORUNUM - sunucu kendi
            // gecmis tahsilatini reddetmiyor, cunku kendi kaydi ona
            // hassas veri degil.
            disabled={kendisi}
          />
          {!kendisi && subeler.length > 1 && (
            <Select
              allowClear
              placeholder="Tüm şubeler"
              style={{ minWidth: 180 }}
              value={sube}
              onChange={(v) => setSube(v || null)}
              options={subeler.map((s) => ({ value: s.id, label: s.name }))}
            />
          )}
          {tahsilEden && (
            <Button
              size="small"
              icon={<CloseCircleOutlined />}
              onClick={() => setTahsilEden(null)}
            >
              Tahsil eden filtresini kaldır
            </Button>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={() => getir(sayfa.current, sayfa.pageSize)}
            loading={yukleniyor}
          >
            Yenile
          </Button>
        </Space>
      </Card>

      {paraBirimleri.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {paraBirimleri.map(([kur, o]) => (
            <Card key={kur} size="small" style={{ minWidth: 200 }}>
              <Statistic
                title={`Toplam (${kur})`}
                value={para(o.toplam)}
                suffix={kur}
              />
              <div className="text-xs text-gray-500">{o.adet} tahsilat</div>
            </Card>
          ))}
        </div>
      )}

      {/* MUTABAKATIN ASIL SORUSU: kim ne topladi. Eren Bey elindeki parayi
          kisi kisi karsilastiriyor, bu yuzden kirilim toplamin yaninda
          duruyor. Satira basmak listeyi o kisiye daraltiyor. */}
      {!kendisi && kirilim.length > 0 && (
        <Card size="small" title="Kim ne topladı">
          <div className="flex flex-wrap gap-2">
            {kirilim.map((k) => {
              const secili = tahsilEden === k.receiver?.id;
              return (
                <Card
                  key={k.receiver?.id || 'bilinmiyor'}
                  size="small"
                  hoverable={!!k.receiver}
                  onClick={() => k.receiver && setTahsilEden(secili ? null : k.receiver.id)}
                  style={{
                    minWidth: 190,
                    borderColor: secili ? '#1677ff' : undefined,
                    cursor: k.receiver ? 'pointer' : 'default',
                  }}
                >
                  <div className="font-medium">
                    <UserOutlined className="mr-1" />
                    {adSoyad(k.receiver)}
                  </div>
                  {Object.entries(k.currencies || {}).map(([kur, o]) => (
                    <div key={kur} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {para(o.toplam)} {kur}
                      <span className="text-xs text-gray-500"> · {o.adet} tahsilat</span>
                    </div>
                  ))}
                </Card>
              );
            })}
          </div>
        </Card>
      )}

      <Card size="small">
        <Table
          columns={sutunlar}
          dataSource={kayitlar}
          rowKey="id"
          loading={yukleniyor}
          size="small"
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                description="Bu aralıkta yarışma tahsilatı yok."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          // Siralama BILEREK yok: liste sunucudan sayfali geliyor, istemci
          // siralamasi yalnizca goruneni sirlayip "siraladim" yanilgisi
          // uretirdi (frontend/CLAUDE.md "Siralama"). Sunucu zaman
          // (paid_at) tersine siraliyor - mutabakatta istenen sira bu.
          pagination={{
            ...sayfa,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100],
            showTotal: (t) => `${t} tahsilat`,
            onChange: (p, boyut) => getir(p, boyut),
          }}
        />
      </Card>
    </div>
  );
};

export default CompetitionCollectionsTab;
